import React, { useState } from 'react';
import { Button, message } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import RichTextEditor from '../../../components/RichTextEditor';
import type { RichTextEditorProps } from '../../../components/RichTextEditor';
import type { Program } from '../../../interfaces/CamProgramming.interface';
import { useUpdateProgramMutation } from '../../../redux/api/manufacturing';
import { hasRichTextContent } from '../fields';

interface EmbedProgrammingFeedbackProps {
	program: Program;
	/** Image handlers from `useProgramImages`. */
	images: Pick<RichTextEditorProps, 'onUploadImage' | 'onImportImageUrl'>;
}

/**
 * Notes the programmer keeps while the session is running. Collapsed by
 * default so the narrow Fusion palette stays usable.
 */
const EmbedProgrammingFeedback: React.FC<EmbedProgrammingFeedbackProps> = ({
	program,
	images,
}) => {
	const [open, setOpen] = useState(true);
	const [draft, setDraft] = useState<string | null>(null);
	const [editorKey, setEditorKey] = useState(0);

	const [updateProgram, { isLoading: isSaving }] = useUpdateProgramMutation();

	const saved = program.programmer_feedback ?? '';
	const visible = (html: string) => (hasRichTextContent(html) ? html : '');
	const isDirty = draft !== null && visible(draft) !== visible(saved);

	const toggle = () => {
		if (!open) {
			setDraft(saved);
			setEditorKey((key) => key + 1);
		}
		setOpen((wasOpen) => !wasOpen);
	};

	const handleSave = async () => {
		try {
			await updateProgram({
				id: program.id,
				values: { programmer_feedback: visible(draft ?? saved) },
			}).unwrap();
			message.success('Notes saved');
		} catch {
			message.error('Could not save notes');
		}
	};

	return (
		<section className="embed-feedback">
			<div className="embed-feedback__bar">
				<button
					type="button"
					className="embed-feedback__toggle"
					onClick={toggle}
					aria-expanded={open}
				>
					{open ? <DownOutlined /> : <RightOutlined />}
					<span>Programming Notes</span>
					{!open && hasRichTextContent(saved) ? (
						<span className="embed-feedback__marker">written</span>
					) : null}
				</button>
				{open || isDirty ? (
					<Button
						type="primary"
						disabled={!isDirty}
						loading={isSaving}
						onClick={handleSave}
					>
						Save
					</Button>
				) : null}
			</div>

			{open ? (
				<div className="embed-feedback__body">
					<RichTextEditor
						key={editorKey}
						content={draft ?? saved}
						onChange={setDraft}
						placeholder="Notes while you program… Paste or drop images directly."
						minHeight={120}
						{...images}
					/>
				</div>
			) : null}
		</section>
	);
};

export default EmbedProgrammingFeedback;
